<script setup>

import { ref, reactive, watch, computed, onMounted } from 'vue'
import Dialog from 'primevue/dialog'
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate"
import * as yup from "yup"
import transactionService from '@/services/transaction.service'
import rentingService from '@/services/renting.service'
import taskService from '@/services/task.service'
import Button from 'primevue/button'
import Icon from '@/components/Icon/Icon.vue'
import ProgressSpinner from 'primevue/progressspinner'
import { createToast } from 'mosha-vue-toastify'
import '@vuepic/vue-datepicker/dist/main.css'
import VueDatepicker from '@vuepic/vue-datepicker'
import moment from 'moment'
import enumService from '@/services/enum.service'

const visible = ref(false)
const inProgress = ref(false)
const rentings = ref([])
const tasksType = ref([])
const tasksStatus = ref([])
const taskInitial = {
    uuid: null,
    name: null,
    status: null,
    type: null,
    date: null,
    due_date: null,
    comment: null,
}
const taskInventoryInitial = {
    renting_uuid: null,
    appointment_datetime: null,
    passage: null,
}
const taskReferenceIndexInitial = {
    renting_uuid: null,
}
let task = reactive({...taskInitial})
let taskInventory = reactive({...taskInventoryInitial})
let taskReferenceIndex = reactive({...taskReferenceIndexInitial})

const schema = yup.object().shape({
    name: yup.string().trim()
        .typeError("Le nom doit être rempli")
        .required("Le nom est obligatoire"),
    date: yup.string().trim()
        .typeError("La date doit être remplie")
        .required("La date est obligatoire"),
    renting_uuid: yup.string().trim()
            .typeError("La location doit être remplie")
            .required("La location est obligatoire"),
    /*renting_uuid: yup.string().trim()
        .when("type", {
            is: "reference_index",
            then: yup.string().required("Must enter renting")
        }),*/
})

const emit = defineEmits(['addTask', 'updateTask'])

const isAdd = computed(() => {
    return (task.uuid === undefined || task.uuid === null) ? true : false
})

watch(visible, (newVal) => {
    if(newVal == false) {
        Object.assign(task, taskInitial);
    }
})

const open = (taskValue) => {
    visible.value = true

    task = Object.assign(task, taskValue)

    switch (task.type) {
        case 'inventory':
            taskInventory = Object.assign(taskInventory, taskValue.tsi)
            break

        case 'reference_index':
            taskReferenceIndex = Object.assign(taskReferenceIndex, taskValue.tri)
            break
    }

    rentingService.getWithProperty().then(data => {
        rentings.value = data.data.rentings
    })

    enumService.get('task-status')
    .then(response => {
        tasksStatus.value = response.data.task_status
    })

    enumService.get('task-type')
    .then(response => {
        tasksType.value = response.data.task_type
    })
}

const close = () => {
    visible.value = false
}

defineExpose({ open, close })

const textInputOptions = ref({
    format: 'dd/MM/yyyy'
})

const handleStartedAt = (date) => {
    const dateVal = moment(date)
    if(dateVal.isValid()) {
        date.value = dateVal.format('YYYY-MM-DD')
    } else {
        date.value = null
    }
}

const isDisabled = (task) => {
    return task.uuid !== undefined && task.uuid !== null
}

const getHeader = () => {
    return (task.uuid === undefined || task.uuid === null) ? "Ajouter une tâche" : "Modifier une tâche"
}

const handleSave = (form) => {
    inProgress.value = true

    const date = moment(form.date)
    if(date.isValid()) {
        form.date = date.format('YYYY-MM-DD')
    }

    const dueDate = moment(form.due_date)
    if(dueDate.isValid()) {
        form.due_date = dueDate.format('YYYY-MM-DD')
    }

    if(form.uuid === undefined || form.uuid === null) {
        delete form.uuid

        taskService.create(form).then(data => {
            emit('addTask', Object.assign({}, data,))

            inProgress.value = false

            close()

            createToast('La tâche a été ajoutée',
            {
                type: 'success',
                showIcon: false,
                hideProgressBar: true,
                timeout: 4000,
            })
        }).catch(e => {
            inProgress.value = false

            createToast("Erreur pendant l'ajout",
            {
                type: 'danger',
                showIcon: false,
                hideProgressBar: true,
                timeout: 4000,
            })
        })
    } else {
        taskService.modify(form.uuid, form).then(data => {
            emit('updateTask', Object.assign({}, data))

            inProgress.value = false

            close()

            createToast('La tâche a été mise à jour',
            {
                type: 'success',
                showIcon: false,
                hideProgressBar: true,
                timeout: 4000,
            })
        }).catch(e => {
            inProgress.value = false

            console.log(e)

            createToast('Erreur pendant la mise à jour',
            {
                type: 'danger',
                showIcon: false,
                hideProgressBar: true,
                timeout: 4000,
            })
        })
    }
}

onMounted(() => {
    rentingService.getWithProperty()
    .then((response) => {
        rentings.value = response.data.rentings
    })
})

</script>

<template>
    <div>
        <Dialog
            v-model:visible="visible"
            modal
            :header="getHeader()"
            :style="{ width: '42rem' }"
        >
            <template #header>
                <div class="w-full px-4 py-2 cursor-move text-2xl border-b border-rose-400">
                    <span class="font-bold whitespace-nowrap">{{ getHeader() }}</span>
                </div>
            </template>
            
            <div class="mx-4">
                <div class="m-0 px-8 py-8 relative bg-white">
                    <VeeForm @submit="handleSave" :validation-schema="schema" class="flex flex-col gap-4">
                        <Field id="uuid" v-model="task.uuid" autocomplete="off" name="uuid" class="w-full form-control form-input" type="hidden" />

                        <div class="flex items-center gap-4 mb-2">
                            <label for="type" class="font-semibold w-56">Type</label>
                            <Field
                                name="type"
                                as="select"
                                class="w-full form-control form-input p-2 bg-white"
                                v-model="task.type"
                                :value="task.type"
                                :disabled="isDisabled(task)"
                            >
                                <option value="" disabled>Sélectionner le type</option>
                                <option :value="null">Aucun</option>
                                <option
                                    v-for="taskType in tasksType"
                                    :key="taskType.id"
                                    :value="taskType.value"
                                >{{ taskType.name }}</option>
                            </Field>
                        </div>
                        <ErrorMessage name="type" v-slot="{ message }">
                            <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                {{ message }}
                            </div>
                        </ErrorMessage>

                        <div class="flex items-center gap-4 mb-2">
                            <label for="name" class="font-semibold w-56">Nom</label>
                            <Field id="name" v-model="task.name" name="name" class="w-full form-control form-input" type="text" />
                        </div>
                        <ErrorMessage name="name" v-slot="{ message }">
                            <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                {{ message }}
                            </div>
                        </ErrorMessage>

                        <div class="flex items-center gap-4 mb-2">
                            <label for="status" class="font-semibold w-56">Statut</label>
                            <Field
                                name="status"
                                as="select"
                                class="w-full form-control form-input p-2 bg-white"
                                v-model="task.status"
                                :value="task.status"
                            >
                                <option value="" disabled>Sélectionner le statut</option>
                                <option
                                    v-for="taskStatus in tasksStatus"
                                    :key="taskStatus.id"
                                    :value="taskStatus.value"
                                >{{ taskStatus.name }}</option>
                            </Field>
                        </div>
                        <ErrorMessage name="status" v-slot="{ message }">
                            <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                {{ message }}
                            </div>
                        </ErrorMessage>

                        <div class="flex items-center gap-4 mb-2">
                            <label for="date" class="font-semibold w-56">Date</label>
                            <Field id="date" v-model="task.date" name="date" class="w-full form-control form-input" type="hidden" />
                            <VueDatepicker
                                v-model="task.date"
                                name="date_picker"
                                :clearable="true"
                                :enableTimePicker="false"
                                locale="fr"
                                cancelText="Annuler"
                                selectText="Valider"
                                format="dd/MM/yyyy"
                                :typeable="true"
                                text-input
                                :text-input-options="textInputOptions"
                                auto-apply
                                class="form-input-datepicker"
                                :auto-position="false"
                                @update:model-value="handleStartedAt"
                            />
                        </div>
                        <ErrorMessage name="date" v-slot="{ message }">
                            <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                {{ message }}
                            </div>
                        </ErrorMessage>

                        <div class="flex items-center gap-4 mb-2">
                            <label for="due_date" class="font-semibold w-56">Date d'expiration</label>
                            <Field id="due_date" v-model="task.due_date" name="due_date" class="w-full form-control form-input" type="hidden" />
                            <VueDatepicker
                                v-model="task.due_date"
                                name="date_picker"
                                :clearable="true"
                                :enableTimePicker="false"
                                locale="fr"
                                cancelText="Annuler"
                                selectText="Valider"
                                format="dd/MM/yyyy"
                                :typeable="true"
                                text-input
                                :text-input-options="textInputOptions"
                                auto-apply
                                class="form-input-datepicker"
                                :auto-position="false"
                                @update:model-value="handleStartedAt"
                            />
                        </div>
                        <ErrorMessage name="due_date" v-slot="{ message }">
                            <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                {{ message }}
                            </div>
                        </ErrorMessage>

                        <div class="flex items-center gap-4 mb-2">
                            <label for="comment" class="font-semibold w-56">Commentaire</label>
                            <Field as="textarea" cols="30" rows="3" id="comment" v-model="task.comment" name="comment" class="w-full form-control form-input" type="text" />
                        </div>
                        <ErrorMessage name="comment" v-slot="{ message }">
                            <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                {{ message }}
                            </div>
                        </ErrorMessage>

                        <div v-if="task.type == 'inventory'" class="flex flex-col gap-4">
                            <div class="flex items-center gap-4 mb-2">
                                <label for="renting_uuid" class="font-semibold w-56">Location</label>
                                <Field
                                    name="renting_uuid"
                                    as="select"
                                    class="w-full form-control form-input p-2 bg-white"
                                    v-model="taskInventory.renting_uuid"
                                    :value="taskInventory.renting_uuid"
                                >
                                    <option value="" disabled>Sélectionner la location</option>
                                    <option
                                        v-for="renting in rentings"
                                        :key="renting.uuid"
                                        :value="renting.uuid"
                                        :selected="renting.uuid === taskInventory.renting_uuid"
                                    >{{ renting.p.name }}</option>
                                </Field>
                            </div>
                            <ErrorMessage name="renting_uuid" v-slot="{ message }">
                                <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                    {{ message }}
                                </div>
                            </ErrorMessage>

                            <div class="flex items-center gap-4 mb-2">
                                <label for="appointment_datetime" class="font-semibold w-56">Rendez-vous</label>
                                <Field id="appointment_datetime" v-model="taskInventory.appointment_datetime" name="appointment_datetime" class="w-full form-control form-input" type="hidden" />
                                <VueDatepicker
                                    v-model="taskInventory.appointment_datetime"
                                    name="date_picker"
                                    :clearable="true"
                                    :enableTimePicker="false"
                                    locale="fr"
                                    cancelText="Annuler"
                                    selectText="Valider"
                                    format="dd/MM/yyyy"
                                    :typeable="true"
                                    text-input
                                    :text-input-options="textInputOptions"
                                    auto-apply
                                    class="form-input-datepicker"
                                    :auto-position="false"
                                    @update:model-value="handleStartedAt"
                                />
                            </div>
                            <ErrorMessage name="appointment_datetime" v-slot="{ message }">
                                <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                    {{ message }}
                                </div>
                            </ErrorMessage>

                            <div class="flex items-center gap-8 mb-2">
                                <label for="passage" class="font-semibold w-56">Passage</label>
                                <div class="flex gap-8 w-full">
                                    <div class="flex gap-4">
                                        <Field id="passage_in" v-model="taskInventory.passage" :value="0" name="passage" class="peer/passage_in" type="radio" />
                                        Entrée
                                    </div>
                                    <div class="flex gap-4">
                                        <Field id="passage_out" v-model="taskInventory.passage" :value="1" name="passage" class="peer/passage_out" type="radio" />
                                        Sortie
                                    </div>
                                </div>
                            </div>
                            <ErrorMessage name="passage" v-slot="{ message }">
                                <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                    {{ message }}
                                </div>
                            </ErrorMessage>
                        </div>

                        <div v-else-if="task.type == 'reference_index'" class="flex flex-col gap-4">
                            <div class="flex items-center gap-4 mb-2">
                                <label for="renting_uuid" class="font-semibold w-56">Location</label>
                                <Field
                                    name="renting_uuid"
                                    as="select"
                                    class="w-full form-control form-input p-2 bg-white"
                                    v-model="taskReferenceIndex.renting_uuid"
                                    :value="taskReferenceIndex.renting_uuid"
                                >
                                    <option value="" disabled>Sélectionner la location</option>
                                    <option
                                        v-for="renting in rentings"
                                        :key="renting.uuid"
                                        :value="renting.uuid"
                                        :selected="renting.uuid === taskReferenceIndex.renting_uuid"
                                    >{{ renting.p.name }}</option>
                                </Field>
                            </div>
                            <ErrorMessage name="renting_uuid" v-slot="{ message }">
                                <div class="border border-red-400 text-red-700 px-4 py-0 mt-1 rounded relative" role="alert">
                                    {{ message }}
                                </div>
                            </ErrorMessage>
                        </div>


                        <div class="flex justify-center gap-4">
                            <Button type="button" @click="close()" class="px-4 py-2 mt-8 w-32 flex gap-2 justify-center shadow-md border border-black rounded hover:shadow-none hover:bg-gray-100">
                                <Icon name="cancel" :size="12" class="w-4 h-4" /> Annuler
                            </Button>
                            <Button v-if="isAdd" :disabled="inProgress" type="submit" class="px-4 py-2 mt-8 w-32 flex gap-2 justify-center shadow-md border border-black bg-green-300 rounded hover:shadow-none hover:bg-green-500">
                                <span v-if="!inProgress">
                                    <Icon name="add" :size="12" class="w-4 h-4" /> Ajouter
                                </span>
                                <ProgressSpinner
                                    v-else
                                    style="width: 20px; height: 20px"
                                    strokeWidth="5"
                                    fill="transparent"
                                    animationDuration="2.5s"
                                    aria-label="Custom ProgressSpinner"
                                    class="m-auto"
                                />
                            </Button>
                            <Button v-else :disabled="inProgress" type="submit" class="px-4 py-2 mt-8 w-32 flex gap-2 justify-center shadow-md border border-black bg-green-300 rounded hover:shadow-none hover:bg-green-500">
                                <span v-if="!inProgress">
                                    <Icon name="edit" :size="12" class="w-4 h-4" /> Modifier
                                </span>
                                <ProgressSpinner
                                    v-else
                                    style="width: 20px; height: 20px"
                                    strokeWidth="5"
                                    fill="transparent"
                                    animationDuration="2.5s"
                                    aria-label="Custom ProgressSpinner"
                                    class="m-auto"
                                />
                            </Button>
                        </div>
                    </VeeForm>
                </div>
            </div>
        </Dialog>
    </div>
</template>
